import React, { useEffect } from 'react';
import { Box, Paper, Typography, Divider, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

// Import custom components
import AddProductSection from './calculator/products/AddProductSection';
import PriceSummary from './calculator/PriceSummary';
import BusinessPhone from './calculator/products/BusinessPhone/BusinessPhone';
import CallTracking from './calculator/products/CallTracking/CallTracking';
import AIConversation from './calculator/products/AIConversation/AIConversation';
import Callback from './calculator/products/Callback/Callback';
import PhoneNumbers from './calculator/products/PhoneNumbers';
import VoiceTraffic from './calculator/products/VoiceTraffic';
import OmnichannelChat from './calculator/products/OmnichannelChat/OmnichannelChat';
import EniqAI from './calculator/products/EniqAI';
import VoiceBot from './calculator/products/VoiceBot';
import CallTrackingSettingsModal from './calculator/modals/CallTrackingSettingsModal';
import AISettingsModal from './calculator/modals/AISettingsModal';
import SubscriptionSettingsDialog from './calculator/modals/SubscriptionSettingsDialog';
import BusinessPhoneSettingsDialog from './calculator/modals/BusinessPhoneSettingsDialog';

// Import context
import { useCalculator } from '../contexts/CalculatorContext';

/**
 * Main Calculator component that uses the CalculatorContext
 */
const CostCalculator = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const {
    // Refs
    summaryRef,
    summaryStyle,
    
    // Products
    productOptions,
    selectedProductsData,
    isProductSelected,
    toggleProduct,
    
    // Phone Numbers
    phoneNumbers,
    setPhoneNumbers, // Add this line
    addPhoneNumber,
    removePhoneNumber,
    updatePhoneNumber,
    phoneNumbersCost,
    phoneNumbersNeeded,
    setPhoneNumbersNeeded,
    
    // Voice Traffic
    voiceTrafficCost,
    
    // Callback
    callbackCost,
    
    // Price Summary
    totalCost,
    
    // Business Phone
    businessPhoneCost,
    businessPhoneType,
    packageTier,

    // Call Tracking
    callTrackingCost,
    callTrackingSettingsOpen,
    hasCustomSettings,
    hasCallTrackingAnyCustomSettings,
    setCallTrackingSettingsOpen,
    callTrackingType,
    callMeButtonEnabled, // Add this line
    staticSources,
    setStaticSources,
    numberFormatsQuantity,
    handleApplyCallTrackingSettings,
    
    // AI Conversation Intelligence
    aiConversationIntelligenceCost,
    aiSettingsOpen,
    hasAiCustomSettings,
    setAiSettingsOpen,
    handleApplyAISettings,
    nonEnglishAnalysis,
    handleApplySubscriptionSettings,

    // Business Phone
    businessPhoneSettingsOpen,
    setBusinessPhoneSettingsOpen,

    // Global Settings
    subscriptionSettingsOpen,
    setSubscriptionSettingsOpen,
    subscription90Days,
    subscription180Days,
    subscription12Month,
    subscriptionSplitPayment,
    customDiscount360,
    customDiscount180,
    region,
    
    // Currency
    currency,

    // Summary
    summaryItems,
    summaryItemsForSubscription,
    total,
    subscriptionPrices,

    // Tab specific ID
    instanceTabId
  } = useCalculator();


  // If instanceTabId is not yet defined (e.g., on initial load when activeTab is still null),
  // show a loader. This prevents the "Rendered more hooks..." error.
  if (!instanceTabId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle opening subscription settings dialog
  const handleOpenSubscriptionSettings = () => {
    setSubscriptionSettingsOpen(true);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Modals and Dialogs */}
      <CallTrackingSettingsModal
        open={callTrackingSettingsOpen}
        onClose={() => setCallTrackingSettingsOpen(false)}
        onApply={handleApplyCallTrackingSettings}
        callTrackingType={callTrackingType}
        callMeButtonEnabled={callMeButtonEnabled}
        staticSources={staticSources}
        numberFormatsQuantity={numberFormatsQuantity}
        hasCustomSettings={hasCustomSettings}
      />
      <AISettingsModal
        open={aiSettingsOpen}
        onClose={() => setAiSettingsOpen(false)}
        onApply={handleApplyAISettings}
        initialSettings={{
          nonEnglishAnalysis,
          hasAiCustomSettings,
        }}
      />
      <BusinessPhoneSettingsDialog
        open={businessPhoneSettingsOpen}
        onClose={() => setBusinessPhoneSettingsOpen(false)}
      />
      <SubscriptionSettingsDialog
        open={subscriptionSettingsOpen}
        onClose={() => setSubscriptionSettingsOpen(false)}
        onApply={handleApplySubscriptionSettings}
        subscription90Days={subscription90Days}
        subscription180Days={subscription180Days}
        subscription12Month={subscription12Month}
        subscriptionSplitPayment={subscriptionSplitPayment}
        customDiscount360={customDiscount360}
        customDiscount180={customDiscount180}
        region={region}
        monthlyTotal={total}
      />
      
      {/* Main Layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 420px' }, gap: 3.5 }}>
        {/* Products Column */}
        <Box>
          <AddProductSection
            productOptions={productOptions()}
            isProductSelected={isProductSelected}
            toggleProduct={toggleProduct}
            packageTier={packageTier}
          />
          {selectedProductsData.map((product) => {
            // Business Phone component
            if (product.id === 'business-phone' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  },
                  mb: 3,
                  p: { xs: 3, md: 4 }
                }}>
                  <BusinessPhone
                    product={product}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Call Tracking component
            else if (product.id === 'call-tracking' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{ 
                  bgcolor: 'white', 
                  borderRadius: theme.spacing(1), 
                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                  boxShadow: 'none', 
                  '&:hover': {
                    boxShadow: theme.shadows[2], 
                  },
                  mb: 3, 
                  p: 3 
                }}>
                  <CallTracking
                    product={product}
                    toggleProduct={toggleProduct}
                    hasCustomSettings={hasCallTrackingAnyCustomSettings}
                    openSettings={() => setCallTrackingSettingsOpen(true)}
                    callTrackingType={callTrackingType}
                    staticSources={staticSources}
                    setStaticSources={setStaticSources}
                    phoneNumbersNeeded={phoneNumbersNeeded}
                  />
                </Paper>
              );
            }
            // AI Conversation Intelligence component
            else if (product.id === 'ai-conversation' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{ 
                  bgcolor: 'white', 
                  borderRadius: theme.spacing(1), 
                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                  boxShadow: 'none', 
                  '&:hover': {
                    boxShadow: theme.shadows[2], 
                  },
                  mb: 3, 
                  p: 3 
                }}>
                  <AIConversation
                    {...product}
                    price={aiConversationIntelligenceCost}
                    hasCustomSettings={hasAiCustomSettings}
                    onOpenSettings={() => setAiSettingsOpen(true)}
                    currency={currency}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Voice Traffic component
            else if (product.id === 'voice-traffic' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{ 
                  bgcolor: 'white', 
                  borderRadius: theme.spacing(1), 
                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                  boxShadow: 'none', 
                  '&:hover': {
                    boxShadow: theme.shadows[2], 
                  },
                  mb: 3, 
                  p: 3 
                }}>
                  <VoiceTraffic
                    product={product}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Callback component
            else if (product.id === 'callback' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{ 
                  bgcolor: 'white', 
                  borderRadius: theme.spacing(1), 
                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                  boxShadow: 'none', 
                  '&:hover': {
                    boxShadow: theme.shadows[2], 
                  },
                  mb: 3, 
                  p: 3 
                }}>
                  <Callback
                    {...product}
                    price={callbackCost}
                    currency={currency}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Omnichannel Chat component
            else if (product.id === 'omnichannel-chat' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{ 
                  bgcolor: 'white', 
                  borderRadius: theme.spacing(1), 
                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                  boxShadow: 'none', 
                  '&:hover': {
                    boxShadow: theme.shadows[2], 
                  },
                  mb: 3, 
                  p: 3 
                }}>
                  <OmnichannelChat
                    product={product}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Eniq AI component
            else if (product.id === 'eniq-ai' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{ 
                  bgcolor: 'white', 
                  borderRadius: theme.spacing(1), 
                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                  boxShadow: 'none', 
                  p: 3, 
                  mb: 3 
                }}>
                  <EniqAI />
                </Paper>
              );
            }
            // VoiceBot component
            else if (product.id === 'voicebot' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  p: 3,
                  mb: 3
                }}>
                  <VoiceBot />
                </Paper>
              );
            }
            // Default case (should never happen)
            return null;
          })}
          {/* Phone Numbers Section (Rendered separately) */}
          {selectedProductsData.find(p => p.id === 'phone-numbers')?.active && (
            <Paper elevation={0} sx={{ 
              bgcolor: 'white', 
              borderRadius: theme.spacing(1), 
              border: '1px solid rgba(0, 0, 0, 0.12)', 
              boxShadow: 'none', 
              '&:hover': {
                boxShadow: theme.shadows[2], 
              },
              mb: 3, 
              p: 3 
            }}>
              <PhoneNumbers
                id="phone-numbers" 
                phoneNumbers={phoneNumbers}      // Use state from usePhoneNumbers (via useCalculator)
                setPhoneNumbers={setPhoneNumbers} // Use state from usePhoneNumbers (via useCalculator)
                currency={currency}
                toggleProduct={toggleProduct}    // Use toggle from useProducts (via useCalculator)
              />
            </Paper>
          )}
        </Box>
        
        {/* Summary Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <PriceSummary 
            summaryItems={summaryItems}
            summaryItemsForSubscription={summaryItemsForSubscription}
            total={total}
            currency={currency}
            summaryRef={summaryRef}
            summaryStyle={summaryStyle}
            subscriptionPrices={subscriptionPrices}
            show90DaySubscription={subscription90Days}
            show180DaySubscription={subscription180Days}
            show12MonthContract={subscription12Month}
            showSplitPayment={subscriptionSplitPayment}
            region={region} // Region for conditional display of subscription options
            onSubscriptionSettingsClick={handleOpenSubscriptionSettings}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CostCalculator;
